/** Shared Rate Card draft types for preview + validation. */

export type MarkupTierDraft = {
    min_amount: string;
    max_amount: string | null;
    multiplier: string;
    sort_order?: number;
};

export type ExtinguisherDraft = {
    base_price: string;
    base_count: number;
    price_per_additional: string;
};

/** HOURLY REPAIR QUOTING RULE — labor rule matched by device size. */
export type RepairLaborRuleDto = {
    min_size_in?: number;
    max_size_in?: number | null;
    hours: number;
    techs?: number;
};

export type PricingDraft = {
    billing_mode: "flat_rate" | "time_and_materials";
    hourly_labor_rate: string | null;
    after_hours_multiplier: string | null;
    hourly_labor_rate_after_hours: string | null;
    test_fee_flat: string | null;
    test_fee_fireline_main: string | null;
    materials_pricing_mode: "at_list" | "at_cost_plus" | "fixed_discount_off_list" | "tiered_markup" | null;
    materials_markup_pct: string | null;
    materials_discount_pct: string | null;
    /** 'pricebook' (default) or 'hourly' — how repair auto-quotes are shaped. */
    repair_quote_mode?: "pricebook" | "hourly" | null;
    /** #1120: three repair tiers per failed device, or only the recommended one. */
    repair_options_mode?: "three" | "single" | null;
    /** Whether a replacement reads as its parts or as one line. Presentation
     *  only — both forms total the identical figure. */
    estimate_line_detail?: "itemised" | "lumped" | null;
    repair_labor_rules?: RepairLaborRuleDto[] | null;
    /** FALSE when this customer does not pay the city's pass-through fees —
     *  test/filing fees and surcharges (migration 601; the admin fee has its
     *  own switch since #1823, the permit since migration 700). Undefined on
     *  older payloads = billed. */
    bill_jurisdiction_fees?: boolean | null;
    /** FALSE when this customer does not pay our Test Reporting & Compliance
     *  (admin) fee — every *AdminFee item (#1823). Undefined = charged. */
    charge_admin_fee?: boolean | null;
    /** FALSE when this customer does not pay the city's permit for replacing
     *  or installing a device (migration 700). Undefined = charged. */
    charge_permit_fee?: boolean | null;
    rate_agreed_on?: string | null;
    rate_term_months?: number | null;
    rate_expires_on?: string | null;
    rate_review_lead_days?: number | null;
    rate_reviewed_at?: string | null;
    rate_review_status?: "none" | "ok" | "due" | "overdue";
};

export type RateReviewStatus = "none" | "ok" | "due" | "overdue";

export const RATE_TERM_OPTIONS = [
    { months: 6, label: "6 months" },
    { months: 12, label: "1 year" },
    { months: 24, label: "2 years" },
    { months: 36, label: "3 years" },
] as const;

export function formatTermMonths(months: number | null | undefined): string {
    if (months == null) return "Custom";
    const opt = RATE_TERM_OPTIONS.find((o) => o.months === months);
    return opt?.label ?? `${months} months`;
}

export function formatMaterialsLabel(pricing: Partial<PricingDraft>): string {
    const mode = pricing.materials_pricing_mode ?? "at_list";
    if (mode === "at_cost_plus") {
        const pct = num(pricing.materials_markup_pct);
        return `Cost + ${pct}% markup`;
    }
    if (mode === "fixed_discount_off_list") {
        const pct = num(pricing.materials_discount_pct);
        return `${pct}% off list`;
    }
    if (mode === "tiered_markup") return "Tiered cost markup";
    return "List price";
}

function addDays(isoDate: string, days: number): string {
    const d = new Date(`${isoDate}T12:00:00`);
    d.setDate(d.getDate() + days);
    return d.toISOString().slice(0, 10);
}

export function computeReviewStatus(pricing: Partial<PricingDraft>, asOf: Date = new Date()): RateReviewStatus {
    if (pricing.rate_review_status && pricing.rate_review_status !== "none") {
        // Prefer server-computed when available (GET pricing).
        if (["ok", "due", "overdue", "none"].includes(pricing.rate_review_status)) {
            return pricing.rate_review_status;
        }
    }
    const expires = pricing.rate_expires_on;
    if (!expires) return "none";
    const lead = pricing.rate_review_lead_days ?? 30;
    const today = asOf.toISOString().slice(0, 10);
    if (today >= expires) return "overdue";
    const dueStart = addDays(expires, -lead);
    if (today >= dueStart) return "due";
    return "ok";
}

export type RateCardDraftContext = {
    pricing: Partial<PricingDraft>;
    tiers: MarkupTierDraft[];
    extPricing: ExtinguisherDraft | null;
    trades: string[];
    /** When true, fireline-main fee is configured separately from standard test fee. */
    splitFirelineMainFee?: boolean;
};

function num(v: string | null | undefined, fallback = 0): number {
    if (v == null || v === "") return fallback;
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
}

export function computeCrewLadder(hourlyRate: number, maxCrew = 3): Array<{ crew: number; rate: number }> {
    if (hourlyRate <= 0) return [];
    return Array.from({ length: maxCrew }, (_, i) => ({
        crew: i + 1,
        rate: hourlyRate * (i + 1),
    }));
}

export function computeAfterHoursRate(pricing: Partial<PricingDraft>): number | null {
    const explicit = num(pricing.hourly_labor_rate_after_hours, NaN);
    if (Number.isFinite(explicit) && explicit > 0) return explicit;
    const base = num(pricing.hourly_labor_rate);
    const mult = num(pricing.after_hours_multiplier, 1.5);
    if (base <= 0) return null;
    return base * mult;
}

export function computeMaterialPrice(
    cost: number,
    pricing: Partial<PricingDraft>,
    tiers: MarkupTierDraft[],
): { unit: number; note: string } {
    const mode = pricing.materials_pricing_mode ?? "at_list";
    if (mode === "tiered_markup" && tiers.length > 0) {
        const sorted = [...tiers].sort((a, b) => num(a.min_amount) - num(b.min_amount));
        const tier =
            sorted.find((t) => {
                const min = num(t.min_amount);
                const max = t.max_amount == null || t.max_amount === "" ? Infinity : num(t.max_amount);
                return cost >= min && cost <= max;
            }) ?? sorted[sorted.length - 1];
        const mult = num(tier?.multiplier, 1);
        return { unit: cost * mult, note: `${mult}× on $${cost.toFixed(2)} cost` };
    }
    if (mode === "at_cost_plus") {
        const pct = num(pricing.materials_markup_pct);
        return { unit: cost * (1 + pct / 100), note: `Cost + ${pct}%` };
    }
    if (mode === "fixed_discount_off_list") {
        const pct = num(pricing.materials_discount_pct);
        return { unit: cost * (1 - pct / 100), note: `${pct}% off list (sample uses cost as list)` };
    }
    return { unit: cost, note: "List price (sample uses cost as list)" };
}

export function computeExtinguisherTotal(count: number, config: ExtinguisherDraft | null): number | null {
    if (!config) return null;
    const base = num(config.base_price);
    const baseCount = config.base_count ?? 0;
    const each = num(config.price_per_additional);
    const additional = Math.max(0, count - baseCount);
    return base + additional * each;
}

export type PreviewBlock = {
    title: string;
    lines: string[];
};

export function buildRateCardPreview(ctx: RateCardDraftContext): PreviewBlock[] {
    const blocks: PreviewBlock[] = [];
    const isTM = (ctx.pricing.billing_mode ?? "flat_rate") === "time_and_materials";

    if (!isTM) {
        blocks.push({
            title: "Billing",
            lines: ["Flat-rate customer — pricebook list prices apply (no contract overrides in preview)."],
        });
        return blocks;
    }

    const hourly = num(ctx.pricing.hourly_labor_rate);
    if (hourly > 0 && ctx.trades.length > 0) {
        const ladder = computeCrewLadder(hourly);
        blocks.push({
            title: "Labor ladder",
            lines: ladder.map((r) => `${r.crew} tech${r.crew > 1 ? "s" : ""} → $${r.rate.toFixed(2)}/hr`),
        });
        const ah = computeAfterHoursRate(ctx.pricing);
        if (ah != null) {
            blocks.push({
                title: "After hours",
                lines: [`$${ah.toFixed(2)}/hr`],
            });
        }
    }

    if (ctx.trades.includes("backflow")) {
        const std = num(ctx.pricing.test_fee_flat);
        const fl = num(ctx.pricing.test_fee_fireline_main);
        const lines: string[] = [];
        if (std > 0) lines.push(`Domestic / bypass / irrigation test → $${std.toFixed(2)}`);
        if (ctx.splitFirelineMainFee && fl > 0) lines.push(`Fireline main test → $${fl.toFixed(2)}`);
        else if (std > 0) lines.push(`Fireline main test → $${std.toFixed(2)} (same as standard)`);
        if (lines.length) blocks.push({ title: "Testing", lines });
    }

    if (ctx.trades.length > 0) {
        const sample = computeMaterialPrice(500, ctx.pricing, ctx.tiers);
        blocks.push({
            title: "Materials (sample)",
            lines: [`$500 cost part → $${sample.unit.toFixed(2)} (${sample.note})`],
        });
    }

    if (ctx.trades.includes("fire_ext") && ctx.extPricing) {
        const total8 = computeExtinguisherTotal(8, ctx.extPricing);
        const base = num(ctx.extPricing.base_price);
        const bc = ctx.extPricing.base_count;
        const each = num(ctx.extPricing.price_per_additional);
        blocks.push({
            title: "Extinguishers (sample)",
            lines: [
                `Base: $${base.toFixed(2)} for up to ${bc} units`,
                `Each additional: $${each.toFixed(2)}`,
                total8 != null ? `8 units → $${total8.toFixed(2)} total` : "",
            ].filter(Boolean),
        });
    }

    if (blocks.length === 0) {
        blocks.push({
            title: "Preview",
            lines: ["Select services and enter rates to see a live preview."],
        });
    }

    return blocks;
}
